import Moment from "moment";
import { json2csv } from "json-2-csv";

import vSelect from "vue-select";
import ActionButton from "../../shared/action_button";

import AssetsMap from "./map";
import AssetsGrid from "./grid";
import SearchField from "../../shared/search_field";

export default {
  components: {
    vSelect,
    ActionButton,
    AssetsMap,
    AssetsGrid,
    SearchField
  },

  props: {
    app: Object,
    data: Object,
    googleMapApiKey: String,
    policyUpdate: Boolean
  },

  data: function () {
    return {
      textSearch: "",

      fulcrumApp: {
        name: '',
        statusField: {},
        fields: [],
        dashboardGroups: [],
        lastSync: '',
        updatedAt: ''
      },

      dataFilter: {
        searchDataFields: [],
        filterDataFields: []
      },

      gridColumns: [],

      filter: {},
      defaultFilter: {},
      allAssets: [],
      filteredAssets: [],
      dataGroups: [],

      isFilterSidebarShown: true,
      screenViewMode: 'split',
      screenViewHeight: 0,
      screenViewWidth: 0,
      mainContentTop: 0,
      mapHeight: 400,
      gridHeight: 200,
      gridWidth: 500,
      debouncedOnScreenResize: null,

      isInitialized: false,
      isOverlayVisible: true,
      isProgressVisible: false,
      progress: 0,
      isSpinnerVisible: true,
    }
  },

  computed: {
    isFilterEmpty() {
      return this.dataFilter.filterDataFields.every(
        field => !field.selectedOptions || field.selectedOptions.length === 0
      );
    },
    
    isExportEnabled() {
      return this.filteredAssets.length > 0 && this.filteredAssets.every(asset => asset.approved === true);
    }
  },

  watch: {
    app: {
      deep: true,
      immediate: true,
      handler: function(app) {
        if (app.id) { 
          this.fulcrumApp.id = app.id || '';
          this.fulcrumApp.name = app.name || '';
          this.fulcrumApp.statusField = app.statusField || {};
          this.fulcrumApp.fields = app.fields || [];
          this.fulcrumApp.lastSync = app.lastSync || '';
          this.fulcrumApp.updatedAt = app.updatedAt || '';
          this.fulcrumApp.dashboardGroups = app.dashboardGroups || [];
          this.fulcrumApp.dashboardType = app.dashboardType || '';
        }
      }
    },
    data: {
      deep: true,
      immediate: true,
      handler: function(data) {
        if (data.length > 0) {
          setTimeout(() => {
            this.allAssets = data;
          }, 400);
        }
      }
    },
    allAssets: {
      deep: true,
      handler() {
        this.filteredAssets = this.allAssets;

        // Set data groups based on the loaded assets
        this.setDataGroups();
        
        // Extend gridColumns and filterDataFields with dashboard groups
        this.extendGridColumnsWithDataGroups();
        this.extendFilterOptionsWithDataGroups();

        // Set up filter options based on asset data
        this.initializeFilterOptions();

        // Apply initial filters
        this.applyFilters();

        // Update filter options after initial filtering
        this.updateFilterOptions();

        // Check for default filter options from query string
        this.initializeDefaultFilterOptions();

        this.isInitialized = true;
        this.isSpinnerVisible = false;
      }
    },
    screenViewMode: {
      handler() {
        this.setComponentsDimensions()
      }
    },
    screenViewHeight: {
      handler() {
        this.setComponentsDimensions()
      }
    },
    screenViewWidth: {        
      handler() {        
        this.setComponentsDimensions()
      }
    }
  },

  mounted() {
    this.mainContentTop = this.$refs.mainContent.getBoundingClientRect().top;

    this.screenViewWidth = this.$refs.mainContent.offsetWidth;
    this.screenViewHeight = window.innerHeight - this.mainContentTop - 48;

    this.observeScreenResize();
  },

  beforeUnmount() {
    this.unobserveScreenResize();
  },

  methods: {
    normalizeAssets(assets) {
      const commentSummaryFieldNames = new Set();

      return assets.map((asset) => {
        const normalizedAsset = {};
        const parsedCommentSummaryFields = {};

        Object.keys(asset).forEach((key) => {
          normalizedAsset[key] = asset[key];

          if (key !== "comment_summary") {
            return;
          }

          const summary = asset[key];

          if (summary == null || String(summary).trim() === "") {
            return;
          }

          String(summary)
            .split(/\s*\|\|\s*/)
            .forEach((part) => {
              const separatorIndex = part.indexOf(":");

              if (separatorIndex === -1) {
                return;
              }

              const rawFieldName = part.slice(0, separatorIndex).trim();
              const fieldValue = part.slice(separatorIndex + 1).trim();

              if (!rawFieldName) {
                return;
              }

              const normalizedFieldName = rawFieldName
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "_")
                .replace(/^_+|_+$/g, "");

              if (normalizedFieldName) {
                commentSummaryFieldNames.add(normalizedFieldName);
                parsedCommentSummaryFields[normalizedFieldName] = fieldValue;
              }
            });
        });

        return {
          normalizedAsset,
          parsedCommentSummaryFields
        };
      }).map(({ normalizedAsset, parsedCommentSummaryFields }) => {
        commentSummaryFieldNames.forEach((fieldName) => {
          normalizedAsset[fieldName] = Object.prototype.hasOwnProperty.call(parsedCommentSummaryFields, fieldName)
            ? parsedCommentSummaryFields[fieldName]
            : null;
        });

        return normalizedAsset;
      });
    },

    exportToCSV() {
      if (!this.filteredAssets.length) {
        return;
      }

      // Step 1: Split Comment Summary into separate columns
      const normalizedAssets = this.normalizeAssets(this.filteredAssets);

      // Step 2: Remove some fields from export
      const fieldsNotToExport = ['_record_id', '_title', 'inspection_date', '_created_by_name', 'initial_inspection_inspector', 'recordID', 'dataGroup', 'comment_summary'];
      
      // Remove photos from export data
      const photoFieldsNotToExport = new Set();

      normalizedAssets.forEach((asset) => {
        Object.keys(asset).forEach((key) => {
          if (key.includes("_photo")) {
            photoFieldsNotToExport.add(key);
          }
        });
      });

      fieldsNotToExport.push(...photoFieldsNotToExport);
      
      // Step: 3: Build assets for export
      const assetsToExport = normalizedAssets.map(asset => {
        const exportedAsset = {};
        Object.keys(asset).forEach(key => {
          if (!fieldsNotToExport.includes(key)) {
            exportedAsset[key] = asset[key];
          }
        });
        return exportedAsset;
      });

      // Step 4: Export data
      const csvData = json2csv(assetsToExport);
      const csvBlob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      
      const downloadUrl = window.URL.createObjectURL(csvBlob);
      const downloadLink = document.createElement("a");
      
      const appName = (this.fulcrumApp.name || "dashboard-assets")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      
      const dateSuffix = Moment().format("YYYY-MM-DD");

      downloadLink.href = downloadUrl;
      downloadLink.download = `${appName || "dashboard-assets"}-${dateSuffix}.csv`;
      
      document.body.appendChild(downloadLink);
      
      downloadLink.click();
      
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(downloadUrl);
    },

    getMaintenanceIssues() {
      const uniqueRawIssues = Array.from(new Set(this.filteredAssets.map(asset => asset['maintenance_issues']).filter(v => v != null && v !== '')));

      const uniqueIssueValues = new Set();

      for (const rawIssue of uniqueRawIssues) {
        // split on '||' with optional spaces around it
        const parts = String(rawIssue).split(/\s*\|\|\s*/);

        for (const part of parts) {
          // remove everything before and including first ": " if present (per-part)
          const afterColon = part.includes(':') ? part.replace(/^[^:]*:\s*/, '') : part;

          // split on commas (some items are comma-separated without spaces), trim and filter
          const subparts = afterColon
            .split(',')
            .map(p => p.trim())
            .filter(p => p.length > 0);

          for (const p of subparts) {
            if (!uniqueIssueValues.has(p)) uniqueIssueValues.add(p);
          }
        }
      }

      // Return alphabetically sorted array (case-insensitive)
      return Array.from(uniqueIssueValues).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      );
    },

    toggleFilterSidebar() {
      this.isFilterSidebarShown = !this.isFilterSidebarShown;
    },

    setComponentsDimensions() {
      switch (this.screenViewMode) {
        case 'map':
          this.mapHeight = this.screenViewHeight;
          break;
        case 'grid':
          this.gridHeight = this.screenViewHeight - 15;
          break;
        case 'split':
        default:
          this.mapHeight = 400;
          this.gridHeight = this.screenViewHeight - this.mapHeight - 35;
          break;
      }

      this.gridWidth = this.screenViewWidth - 28;
    },

    debounce(fn, delay) {
      let timeout;

      function debounced(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), delay);
      }

      debounced.cancel = function() {
        clearTimeout(timeout);
      };

      return debounced;
    },

    observeScreenResize() {
      if (!this.$refs.mainContent) return;

      this.debouncedOnScreenResize = this.debounce(this.onScreenResize, 100);
      this.debouncedOnViewportResize = this.debounce(this.onViewportResize, 100);

      // Observe mainContent size changes
      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          this.debouncedOnScreenResize(entry.contentRect);
        }
      });
      this.resizeObserver.observe(this.$refs.mainContent);

      // Observe viewport (window) size changes
      window.addEventListener('resize', this.debouncedOnViewportResize);
    },

    unobserveScreenResize() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      if (this.debouncedOnScreenResize) {
        this.debouncedOnScreenResize.cancel();
      }
      if (this.debouncedOnViewportResize) {
        window.removeEventListener('resize', this.debouncedOnViewportResize);
        this.debouncedOnViewportResize.cancel();
      }
    },

    onScreenResize(rect) {
      this.screenViewWidth = rect.width;
    },

    onViewportResize() {
      this.screenViewHeight = window.innerHeight - this.mainContentTop - 48;;
    },

    toggleScreenViewMode(mode) {
      this.screenViewMode = mode;
    },

    setDataGroups() {
      if (Array.isArray(this.fulcrumApp.dashboardGroups) && this.fulcrumApp.dashboardGroups.length > 0) {
        // Filter for dashboard groups that have at least one value in filteredAssets
        this.dataGroups = this.fulcrumApp.dashboardGroups.filter(group => {
          return this.filteredAssets.some(asset => asset[group.data_name] != null && asset[group.data_name] !== '');
        })
      }
    },

    extendGridColumnsWithDataGroups() {
      if (Array.isArray(this.dataGroups) && this.dataGroups.length > 0) {
        const groupColumns = this.dataGroups
          .map(group => ({
            field: group.data_name,
            label: group.label,
            type: "string",
            width: 150,
            align: "left",
            sortable: true
          }));
        if (groupColumns.length > 0) {
          this.gridColumns = [
            this.gridColumns[0],
            ...groupColumns,
            ...this.gridColumns.slice(1)
          ];
        }
      }
    },
    
    extendFilterOptionsWithDataGroups() {
      if (Array.isArray(this.dataGroups) && this.dataGroups.length > 0) {
        const groupFields = this.dataGroups
          .map(group => ({
            key: group.data_name,
            label: group.label,
            placeholder: "Select " + group.label,
            options: [],
            selectedOptions: [],
            type: "select",
            multiple: true
          }));
        const placeholderIndex = this.dataFilter.filterDataFields.findIndex(field => field.type === "dataGroups");

        if (placeholderIndex >= 0) {
          this.dataFilter.filterDataFields = [
            ...this.dataFilter.filterDataFields.slice(0, placeholderIndex),
            ...groupFields,
            ...this.dataFilter.filterDataFields.slice(placeholderIndex + 1)
          ];
        } else {
          this.dataFilter.filterDataFields = [
            ...groupFields,
            ...this.dataFilter.filterDataFields
          ];
        }
      }
    },

    initializeDefaultFilterOptions() {
      const defaultFilter = this.getFilterState();

      if (!defaultFilter) return;

      this.defaultFilter = JSON.parse(defaultFilter);      

      Object.keys(this.defaultFilter).forEach(key => {
        const selectedItems = this.defaultFilter[key];

        if (Array.isArray(selectedItems)) {
          // Find corresponding filterDataField
          const field = this.dataFilter.filterDataFields.find(f => f.key === key);
          
          if (!field || !Array.isArray(field.options)) return;

          // Filter by option.name for object options, or by option value for plain arrays.
          field.selectedOptions = field.options.filter(option => {
            if (option && typeof option === "object" && "name" in option) {
              return selectedItems.includes(option.name);
            }

            return selectedItems.includes(option);
          });
        }
        else if (typeof selectedItems === 'string') {
          const field = this.dataFilter.filterDataFields.find(f => f.key === key);
          if (!field || !Array.isArray(field.options)) return;

          field.selectedOptions = field.options.find(option => {
            if (option && typeof option === "object" && "name" in option) {
              return option.name === selectedItems;
            }

            return option === selectedItems;
          }) || '';
        }
        else if (typeof selectedItems === 'boolean') {
          const field = this.dataFilter.filterDataFields.find(f => f.key === key);

          if (!field || !Array.isArray(field.options)) return;

          return selectedItems === true ? field.selectedOptions = true : [];
        }
      });

      this.applyFilters();
    },
    
    initializeFilterOptions() {
      this.dataFilter.filterDataFields.forEach(item => {
        // For each filter field, if item.model exists and is an object, set options to [{name, values}]
        if (item.model && typeof item.model === 'object' && !Array.isArray(item.model)) {
          item.options = Object.keys(item.model).map(key => ({
            name: key,
            values: item.model[key]
          }));
        }

        // For each filter field, if item.method exists and is an function, call it to get options
        if (item.method && typeof this[item.method] === 'function') {
          item.options = this[item.method]();
        }
      });

      // Create filter object from filter options
      this.dataFilter.filterDataFields.forEach(field => {
        if (this.filter[field.key] === undefined) {
          this.filter[field.key] = field.multiple ? [] : '';
        }
      });
    },

    clearFilter() {
      this.dataFilter.filterDataFields.forEach(field => {
        field.selectedOptions = [];
      });
      
      this.applyFilters();

      app.ui.state.update({
        "view": "data",
        "dashboard": this.fulcrumApp.dashboardType.toLowerCase(),
        "v-filter": null
      });
    },

    updateFilterOptions(updatedKey = null) {
      this.dataFilter.filterDataFields.forEach(field => {
        if (field.key === updatedKey) return; // skip the field just updated
        const key = field.key;
        
        // Get one dimension unique values array
        const uniqueValues = Array.from(
          new Set(
            this.filteredAssets
              .flatMap(asset => {
                const value = asset[key];
                return Array.isArray(value) ? value : [value];
              })
              .filter(v => v != null && v !== '')
          )
        );
        
        if (field.model && typeof field.model === 'object' && !Array.isArray(field.model)) {
          // Only include model entries whose values intersect with uniqueValues
          field.options = Object.keys(field.model)
            .map(name => {
              const values = field.model[name].filter(v => uniqueValues.includes(v));
              return values.length > 0 ? { name, values } : null;
            })
            .filter(Boolean);
        } else if (field.method && typeof this[field.method] === 'function') {
          const allOptions = this[field.method](key);

          field.options = allOptions;
          //field.options = allOptions.filter(option => uniqueValues.includes(option));
        } else {
          field.options = uniqueValues;
        }
      });
    },

    getStateParam: function(param) {
      this.stateParams = new URLSearchParams(window.location.search);

      return this.stateParams.get(param);
    },

    normalizeFilterState(obj) {
      const result = {};

      // Normalize filter array elements based on model
      Object.keys(obj).forEach(key => {
        const value = obj[key];

        if (Array.isArray(value)) {
          // Check if array contains objects with a "name" property
          if (
            value.length > 0 &&
            typeof value[0] === "object" &&
            value[0] !== null &&
            "name" in value[0]
          ) {
            result[key] = value.map(item => item.name);
          } else {
            result[key] = value;
          }
        } else {
          result[key] = value;
        }
      });

      return result;
    },

    getFilterState: function() {
      return this.getStateParam("v-filter");
    },

    setFilterState: function() {
      if (this.isInitialized) {
      
        const filterState = {};

        // Prepare filter and include only non-empty values in state
        this.dataFilter.filterDataFields.forEach(field => {
          const value = field.selectedOptions && (field.selectedOptions.length > 0 || typeof field.selectedOptions === "boolean")
            ? field.selectedOptions
            : field.multiple ? [] : '';

          this.filter[field.key] = value;

          if (typeof value === "string" && value.length === 0) return;
          
          if (Array.isArray(value) && value.length === 0) return;
          
          if (value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0) return;

          if (typeof value === "boolean" && value !== true) return;

          filterState[field.key] = value;
        });

        const normalizedFilterState = this.normalizeFilterState(filterState);

        app.ui.state.update({
          "view": "data",
          "dashboard": this.fulcrumApp.dashboardType.toLowerCase(),
          "v-filter": Object.keys(normalizedFilterState).length <= 0 ? null : JSON.stringify(normalizedFilterState)
        });

        // Trigger UI stateupdade event
        window.dispatchEvent(new Event("updatestate"));
      }
    },

    applyFilters() {
      let filtered = this.allAssets;

      // Apply filter
      this.dataFilter.filterDataFields.forEach(field => {
        if (field.type === "select") {
          if (Array.isArray(field.selectedOptions) && field.selectedOptions.length > 0) {
            let selectedFlat = [];
            
            if (Array.isArray(field.selectedOptions[0])) {
              // Flatten nested arrays (e.g., [[...], [...]])
              selectedFlat = field.selectedOptions.flat();
            } else if (typeof field.selectedOptions[0] === 'object' && field.selectedOptions[0] !== null && field.selectedOptions[0].values) {
              // If selectedOptions are objects with values arrays (from model - e.g., [{name, values}])
              selectedFlat = field.selectedOptions.reduce((acc, obj) => acc.concat(obj.values), []);
            } else {
              // If selectedOptions are array (e.g., ['value1', 'value2'])
              selectedFlat = field.selectedOptions;
            }

            // Filter assets where asset[field.key] matches any of the selected options including arrays
            filtered = filtered.filter(asset => {
              const assetValue = asset[field.key];

              return Array.isArray(assetValue)
                ? assetValue.some(value => selectedFlat.includes(value))
                : selectedFlat.includes(assetValue);
            });

          } else if (typeof field.selectedOptions === 'string' && field.selectedOptions !== '') {
            if (field.filter === 'contains') {
              filtered = filtered.filter(asset => {
                const assetValue = asset[field.key];
                if (assetValue == null || assetValue === '') return false;

                const parts = String(assetValue).split(/\s*\|\|\s*/).map(p => p.trim());

                return parts.some(part => part.toLowerCase().includes(field.selectedOptions.toLowerCase()));
              });
            } else { // exact match
              filtered = filtered.filter(asset => asset[field.key] === field.selectedOptions);
            }
          }
        }
        else if (field.type === "boolean") {
          if (field.selectedOptions === field.value) {
            filtered = filtered.filter(asset => asset[field.key] === true);
          }
        }
      });

      // Apply search
      if (this.textSearch && this.textSearch.trim() !== '') {
        const searchText = this.textSearch.trim().toLowerCase();
        filtered = filtered.filter(asset => {
          return this.dataFilter.searchDataFields.some(key => {
            return String(asset[key]).toLowerCase().includes(searchText);
          });
        });
      }

      this.filteredAssets = filtered;

      //this.updateFilterOptions();

      this.setFilterState();
    },

    search() {
      this.applyFilters()
    },
  },
}
